"use client";

import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { LoginDtoSchema } from '@/lib/validations/auth.validation';
import { authService } from '@/services/auth.service';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowRight, Sparkles, Lock, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

function LoginPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  useEffect(() => {
    if (authService.isAuthenticated()) {
      const role = authService.getUserRole();
      if (role) {
        switch (role.toLowerCase()) {
          case 'student':
            router.push('/student/dashboard');
            return;
          case 'company':
            router.push('/company/dashboard');
            return;
          case 'teacher':
            router.push('/teacher/dashboard');
            return;
          case 'admin':
            router.push('/admin/dashboard');
            return;
        }
      }
    }

    const registered = searchParams.get('registered');
    const verified = searchParams.get('verified');
    const reset = searchParams.get('reset');

    if (registered === 'true') {
      setSuccessMessage('Registration successful! Please check your email to verify your account.');
    } else if (verified === 'true') {
      setSuccessMessage('Email verified! You can now log in.');
    } else if (reset === 'success') {
      setSuccessMessage('Password reset successful! Please log in with your new password.');
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setIsLoading(true);

    try {
      const validatedData = LoginDtoSchema.parse(formData);
      const response = await authService.login(validatedData);
      
      // Redirect based on user role
      const role = response.user.role.toLowerCase();
      switch (role) {
        case 'student':
          router.push('/student/dashboard');
          break;
        case 'company':
          router.push('/company/dashboard');
          break;
        case 'teacher':
          router.push('/teacher/dashboard');
          break;
        case 'admin':
          router.push('/admin/dashboard');
          break;
        default:
          router.push('/');
      }
    } catch (err: any) {
      if (err.errors) {
        const errors: Record<string, string> = {};
        err.errors.forEach((error: any) => {
          errors[error.path[0]] = error.message;
        });
        setFieldErrors(errors);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isEmailValid = formData.email.includes('@') && formData.email.includes('.');

  return (
    <div className="min-h-screen flex">
      <motion.div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid.svg')] bg-repeat"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link href="/">
            <div className="relative w-48 h-16">
              <Image src="/logo.svg" alt="Learn2Link" fill className="object-contain" priority />
            </div>
          </Link>

          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                Welcome back to<br />Learn2Link
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                Access your personalized learning dashboard and continue your journey to success.
              </p>
            </motion.div>

            <motion.div 
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-white mb-2">1000+</div>
                <div className="text-white/80 text-sm">Available Courses</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-white mb-2">500+</div>
                <div className="text-white/80 text-sm">Partner Companies</div>
              </div>
            </motion.div>
          </div>

          <p className="text-white/60 text-sm">© 2025 Learn2Link. All rights reserved.</p>
        </div>
      </motion.div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="lg:hidden mb-8 text-center">
            <Link href="/">
              <div className="relative w-36 h-12 mx-auto">
                <Image src="/logo.svg" alt="Learn2Link" fill className="object-contain" priority />
              </div>
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
            <p className="text-gray-600">Enter your credentials to continue</p>
          </div>

          {successMessage && (
            <motion.div 
              className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-green-800">{successMessage}</span>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div 
              className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                    fieldErrors.email
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#6059f7]'
                  }`}
                  placeholder="you@example.com"
                />
              </div>
              {fieldErrors.email && (
                <p className="mt-2 text-xs text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full pl-11 pr-12 py-3 border-2 rounded-lg focus:outline-none focus:ring-0 transition-colors text-gray-900 ${
                    fieldErrors.password
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#6059f7]'
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-2 text-xs text-red-600">{fieldErrors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-[#6059f7] border-gray-300 rounded focus:ring-[#6059f7] cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">Remember me</span>
              </label>
              <button
                type="button"
                onClick={async () => {
                  if (!isEmailValid) return;
                  try {
                    await authService.forgotPassword({ email: formData.email });
                    sessionStorage.setItem('reset_email', formData.email);
                    router.push('/auth/verify-otp');
                  } catch (err: any) {
                    setError(err.message || 'Failed to send OTP');
                  }
                }}
                className={`text-sm font-semibold transition-colors ${
                  isEmailValid 
                    ? 'text-[#6059f7] hover:text-[#6059f7]/80' 
                    : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                Forgot password?
              </button>
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#6059f7] text-white rounded-lg font-semibold hover:bg-[#5048e8] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#6059f7]/20"
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Don't have an account?</span>
              </div>
            </div>

            <Link href="/auth/signup">
              <motion.button
                type="button"
                className="w-full py-3.5 border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-[#6059f7] hover:text-[#6059f7] hover:bg-[#6059f7]/5 transition-all flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Sparkles className="w-5 h-5" />
                Create new account
              </motion.button>
            </Link>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <LoginPageContent />
    </Suspense>
  );
}
